import { Component, DestroyRef, OnInit, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, forkJoin, Observable } from "rxjs";
import { ISidepanelTab } from "src/app/components/sidepanel-tabs/sidepanel-tabs.component";
import { IntegrationModel } from "src/app/models/api";
import { PolicyModel } from "src/app/models/api/governance/policy.model";
import { RuleCategory, ViolationRule } from "src/app/models/api/governance/violationRule.model";
import { PoliciesHttpService, RuleType } from "src/app/services/httpServices/governance/policiesHttpService/policies-http.service";
import { ViolationRulesHttpService } from "src/app/services/httpServices/governance/violationRulesHttpService/violation-rules-http.service";
import { HttpErrorHandlerService } from "src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service";
import { NotificationsService } from "src/app/services/infra/notifications/notifications.service";
import { StoreService } from "src/app/services/infra/store/store.service";
import "src/app/common/arrayUtils";
import { EnvironmentsHttpService } from "src/app/services/httpServices/governance/environmentsHttpService/environments-http.service";
import { EnvironmentModel } from "src/app/models/api/governance/environment.model";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: "app-policy",
    templateUrl: "./policy.component.html",
    styleUrls: ["./policy.component.scss"],
})
export class PolicyComponent implements OnInit {
    public integrationId: string;
    public policyId: string | null;

    public violationRules: ViolationRule[];
    public hygieneRules: ViolationRule[];
    public policyModel: PolicyModel;
    public integrationModel?: IntegrationModel;

    public isLoading = signal<boolean>(false);
    public currentTabIndex$: Observable<number>;

    public environments = signal<EnvironmentModel[]>([]);

    public RuleTypeEnum = RuleType;

    // tabs
    public tabs: ISidepanelTab[] = [
        { labelPath: "policyPage.policy.violationRulesTab" },
        { labelPath: "policyPage.policy.hygieneRulesTab" },
        // to be enabled when deliver custom policy
        // { labelPath: "policyPage.policy.exemptionRulesTab" },
        { labelPath: "policyPage.policy.configurationTab" },
        { labelPath: "policyPage.policy.inLineControlRulesTab" },
    ];

    private _currentTabIndexSubject = new BehaviorSubject(0);

    constructor(
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _httpErrorHandlerService: HttpErrorHandlerService,
        private _violationRulesHttpService: ViolationRulesHttpService,
        private _policiesHttpService: PoliciesHttpService,
        private _notificationService: NotificationsService,
        private _storeService: StoreService,
        private _environmentsHttpService: EnvironmentsHttpService,
        private _destroyRef: DestroyRef,
    ) {}

    ngOnInit(): void {
        this.currentTabIndex$ = this._currentTabIndexSubject.asObservable();
        this._handleUrlQueryParamsOnChange();
    }

    public onTabSelect(selectedIndex: number) {
        this._currentTabIndexSubject.next(selectedIndex);
    }

    public backToPolicies() {
        this._router.navigate(["app/policies"], {
            queryParams: { integration: this.integrationId },
        });
    }

    public refreshPage() {
        this.isLoading.set(true);
        this._getViolationRules();
        this._getEnvironments();
    }

    private _handleUrlQueryParamsOnChange() {
        this.isLoading.set(true);
        this._activatedRoute.queryParams.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
            next: (params) => {
                const tab = params["tab"];
                if (tab == "configuration") {
                    // Assuming the configuration tab is always last
                    this.onTabSelect(this.tabs.length - 1);
                }

                const integration = params["integration"];
                if (integration) {
                    this.integrationId = integration;
                    this.policyId = this._activatedRoute.snapshot.paramMap.get("id");

                    this._getViolationRules();
                    this._getEnvironments();
                }
            },
            error: (error) => this._httpErrorHandlerService.handle(error),
        });
    }

    private _getViolationRules() {
        if (!(this.integrationId && this.policyId)) {
            this._notificationService.error("No policy ID or integration ID found");
            this.isLoading.set(false);
            return;
        }

        this._storeService.integrations$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((integrations) => {
            this.integrationModel = integrations.find((integration) => integration.id === this.integrationId);
        });
        forkJoin([
            this._policiesHttpService.getPolicy(this.integrationId, this.policyId),
            this._violationRulesHttpService.getGovernanceRules(this.integrationId, this.policyId),
        ])
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(([policyModel, violationRules]) => {
                this.policyModel = policyModel;
                this.violationRules = [];
                this.hygieneRules = [];
                violationRules.forEach((rule) => {
                    if (rule.category !== RuleCategory.hygiene) {
                        this.violationRules.push(rule);
                    } else {
                        this.hygieneRules.push(rule);
                    }
                });
                this.isLoading.set(false);
            });
    }

    private _getEnvironments() {
        this._environmentsHttpService
            .getEnvironments(this.integrationId)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((environmentsModel: EnvironmentModel[]) => {
                this.environments.set(environmentsModel);
            });
    }
}
